import { p256 } from "@noble/curves/nist.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { utf8ToBytes } from "@noble/hashes/utils.js";
import { mnemonicToSeed } from "@scure/bip39";
import {
  isValidSecretPhrase,
  secretPhraseToMnemonic,
  type SecretPhrase,
} from "./secretPhrase";

/**
 * Deterministic P-256 owner-key derivation. Do not change these parameters
 * after wallets are provisioned — the same phrase must always reproduce the
 * same public key.
 *
 *   seed   = BIP-39 PBKDF2-HMAC-SHA512(NFKD(phrase), salt="mnemonic", 2048, 64)
 *   scalar = HKDF-SHA256(seed, salt="", info="myron-wallet-owner-key-v1", 32)
 *            retry info+":1", ":2", … if the scalar is not a valid P-256 secret
 *   pubkey = scalar × G, uncompressed (65 bytes)
 *   send   = base64(DER SPKI wrapper + those 65 bytes)
 */
const OWNER_KEY_INFO_PREFIX = "myron-wallet-owner-key-v1";
const MAX_SCALAR_ATTEMPTS = 8;

/** DER SubjectPublicKeyInfo prefix for an uncompressed P-256 point. */
export const P256_SPKI_PREFIX = Uint8Array.from([
  0x30, 0x59, 0x30, 0x13, 0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01,
  0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07, 0x03, 0x42, 0x00,
]);

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function deriveScalar(seed: Uint8Array): Uint8Array {
  const emptySalt = new Uint8Array();
  for (let attempt = 0; attempt < MAX_SCALAR_ATTEMPTS; attempt += 1) {
    const info =
      attempt === 0
        ? OWNER_KEY_INFO_PREFIX
        : `${OWNER_KEY_INFO_PREFIX}:${attempt}`;
    const scalar = hkdf(sha256, seed, emptySalt, utf8ToBytes(info), 32);
    if (p256.utils.isValidSecretKey(scalar)) return scalar;
    scalar.fill(0);
  }
  throw new Error("Failed to derive a valid P-256 owner key");
}

function encodeSpkiBase64(uncompressedPoint: Uint8Array): string {
  const der = new Uint8Array(P256_SPKI_PREFIX.length + uncompressedPoint.length);
  der.set(P256_SPKI_PREFIX);
  der.set(uncompressedPoint, P256_SPKI_PREFIX.length);
  return bytesToBase64(der);
}

/**
 * Derive the owner public key from a secret phrase.
 * Only the public half is returned — the private scalar is discarded.
 */
export async function deriveOwnerPublicKey(phrase: SecretPhrase): Promise<string> {
  if (!isValidSecretPhrase(phrase)) {
    throw new Error("Invalid secret phrase");
  }

  const seed = await mnemonicToSeed(secretPhraseToMnemonic(phrase));
  const scalar = deriveScalar(seed);
  try {
    const uncompressedPoint = p256.getPublicKey(scalar, false);
    return encodeSpkiBase64(uncompressedPoint);
  } finally {
    scalar.fill(0);
    seed.fill(0);
  }
}
